/**
 * Pool de suggestions de recherche Wikimedia Commons (admin) — pas d’URL figée dans la grille.
 * Les entrées proviennent des mêmes libellés que l’ancienne liste de portraits (référence pour les requêtes).
 */
import { COMPOSER_PORTRAITS, ILLUSTRATION_PORTRAITS } from './avatarPortraitLists'

export const MAX_VISIBLE_COMMONS_SUGGESTIONS = 10

const seenUrl = new Set()
const ALL = []
for (const row of [...COMPOSER_PORTRAITS, ...ILLUSTRATION_PORTRAITS]) {
  if (!row?.url || seenUrl.has(row.url)) continue
  seenUrl.add(row.url)
  ALL.push(row)
}

function slugify(s) {
  const t = String(s || '')
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .replace(/[^a-zA-Z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .toLowerCase()
  return t || 'x'
}

const usedIds = new Set()
function uniqueStableId(base) {
  let id = base
  let n = 0
  while (usedIds.has(id)) {
    n += 1
    id = `${base}-${n}`
  }
  usedIds.add(id)
  return id
}

/** @type {{ id: string, label: string, query: string, priority?: number }[]} */
export const AVATAR_COMMONS_SEARCH_SUGGESTIONS = ALL.map((row) => {
  const cat = row.category === 'composer' ? 'composer' : 'illustration'
  const id = uniqueStableId(`${cat}-${slugify(row.label)}`)
  const query =
    row.category === 'composer' ? `${row.label} portrait composer` : row.label
  return {
    id,
    label: row.label,
    query,
    priority: row.category === 'composer' ? 0 : 1
  }
})

const SUGGESTION_IDS = new Set(AVATAR_COMMONS_SEARCH_SUGGESTIONS.map((s) => s.id))

/**
 * @param {string | undefined | null} id
 * @returns {boolean}
 */
export function isValidAvatarSuggestionId(id) {
  return typeof id === 'string' && id.length > 0 && id.length <= 120 && SUGGESTION_IDS.has(id)
}

/**
 * Suggestions encore utiles (pas déjà liées à une entrée Firestore), triées par priority puis ordre fichier.
 * @param {{ suggestionId?: string }[]} entries
 * @param {{ maxVisible?: number }} [opts]
 * @returns {{ id: string, label: string, query: string, priority?: number }[]}
 */
export function getVisibleAvatarCommonsSuggestions(entries, opts = {}) {
  const maxVisible = opts.maxVisible ?? MAX_VISIBLE_COMMONS_SUGGESTIONS
  const used = new Set(
    (Array.isArray(entries) ? entries : [])
      .map((e) => e?.suggestionId)
      .filter((x) => typeof x === 'string' && x.length > 0)
  )
  const candidates = AVATAR_COMMONS_SEARCH_SUGGESTIONS.filter((s) => !used.has(s.id)).sort(
    (a, b) => (a.priority ?? 99) - (b.priority ?? 99)
  )
  return candidates.slice(0, maxVisible)
}

/**
 * Toutes les suggestions du pool ont une entrée avec le même suggestionId.
 * @param {{ suggestionId?: string }[]} entries
 * @returns {boolean}
 */
export function isAvatarSuggestionPoolFullyUsed(entries) {
  const used = new Set(
    (Array.isArray(entries) ? entries : [])
      .map((e) => e?.suggestionId)
      .filter((x) => typeof x === 'string' && x.length > 0)
  )
  return AVATAR_COMMONS_SEARCH_SUGGESTIONS.length > 0 &&
    AVATAR_COMMONS_SEARCH_SUGGESTIONS.every((s) => used.has(s.id))
}
